/**
 * กิลด์ศัตรู and ป้ายสังหาร (data/guildBanner.ts, docs/design/2026-10-14-guild-enemy-banner.md).
 *
 * - **Enemy list:** the leader or a deputy declares another guild an enemy, one-sided (`guildEnemyAdd` / `Remove`).
 * - **Kill banner:** against an enemy, paid from the coffer, up for `duration` s, then `cooldown` s before the next;
 *   one at a time. While it is up the two guilds' members may fight without PK on, both ways (`pvpHostile`), and
 *   kills between them carry no PK penalty (`pkKill`: no points, no colour, no karma, so no guard aggro). Bystanders
 *   keep every rule; safe zones and newbie protection still hold (the World checks them around the hook).
 * - **Offline build:** the other guilds are simulated, so while the player's banner is up and the player is in the
 *   wilds, members of the hunted guild come hunting (arena contenders under their names), marked, and leave on
 *   death or when the banner ends.
 *
 * Every player's guild is read from their own progress (`guildOf`), so a server runs the same rules for real members.
 */
import { ARENA_ROSTER } from '../data/arena';
import { simGuild } from '../data/guild';
import { GUILD_BANNER as B, type GuildBannerMark } from '../data/guildBanner';
import { getArt, getSkill } from '../data/skills';
import { freeAt } from '../path';
import type { Entity } from '../world';
import { addGuildContribution, type GuildBannerState, type GuildState } from './guild';
import type { SystemHost, WorldSystem } from './types';

export type GuildBannerCommand =
  | { t: 'guildEnemyAdd'; id: string }
  | { t: 'guildEnemyRemove'; id: string }
  | { t: 'guildBannerRaise'; id: string };

export type GuildBannerEvent =
  | { type: 'guildBanner'; what: 'enemyAdd' | 'enemyRemove'; guild: string }
  | { type: 'guildBanner'; what: 'raise'; guild: string; target: string; left: number }
  | { type: 'guildBanner'; what: 'kill'; killer: string; victim: string; ours: number; theirs: number }
  | { type: 'guildBanner'; what: 'end'; guild: string; target: string; ours: number; theirs: number }
  | { type: 'guildBanner'; what: 'foe'; id: number; name: string };

const people = (e: Entity) => e.kind === 'player' || e.kind === 'rival';

export class GuildBannerSystem implements WorldSystem {
  readonly name = 'guildBanner';
  /** Simulated hunters in the world: entity id → their guild id. */
  private readonly foes = new Map<number, string>();
  private foeT: number = B.foeEvery / 3;

  constructor(private readonly h: SystemHost) {}

  // ───────────────────────── who is in which guild ─────────────────────────

  /** Every player's guild in the zone (one state per player; a server's members carry copies). */
  private guilds(): GuildState[] {
    const out: GuildState[] = [];
    for (const s of this.h.world.players.values()) if (s.progress.guild) out.push(s.progress.guild);
    return out;
  }

  /** The guild id an entity belongs to: a player's own, a simulated hunter's, else null. */
  guildOf(e: Entity): string | null {
    if (e.kind === 'player') return this.h.world.players.get(e.id)?.progress.guild?.id ?? null;
    return this.foes.get(e.id) ?? null;
  }

  /** A guild's name by id: a player's guild in the zone, or a simulated one. */
  private nameOf(id: string): string {
    return this.guilds().find((g) => g.id === id)?.name ?? simGuild(id)?.name ?? id;
  }

  /** The banner up between two guilds (either way round), with the guild that raised it. */
  private between(ga: string | null, gb: string | null): { owner: GuildState; banner: GuildBannerState } | null {
    if (!ga || !gb || ga === gb) return null;
    for (const g of this.guilds()) {
      const b = g.banner;
      if (!b || b.left <= 0) continue;
      if ((g.id === ga && b.target === gb) || (g.id === gb && b.target === ga)) return { owner: g, banner: b };
    }
    return null;
  }

  /** True while a banner stands between these two people's guilds. */
  bannerFoes(a: Entity, b: Entity): boolean {
    return a !== b && people(a) && people(b) && !!this.between(this.guildOf(a), this.guildOf(b));
  }

  /** What the current player sees over `e`'s head: the hunted of our banner, or a hunter of a banner against us. */
  markOf(e: Entity): GuildBannerMark | null {
    const mine = this.h.world.progress.guild;
    if (!mine || e === this.h.world.player || !people(e)) return null;
    const theirs = this.guildOf(e);
    if (!theirs || theirs === mine.id) return null;
    if (mine.banner && mine.banner.left > 0 && mine.banner.target === theirs) return 'hunted';
    return this.between(mine.id, theirs) ? 'hunter' : null;
  }

  // ───────────────────────── World hooks ─────────────────────────

  pvpHostile(a: Entity, b: Entity): boolean {
    return this.bannerFoes(a, b);
  }

  pkKill(killer: Entity, victim: Entity): boolean {
    const w = this.h.world;
    if (w.inSafeZone(killer) || w.inSafeZone(victim)) return false;
    const gk = this.guildOf(killer);
    const pair = this.between(gk, this.guildOf(victim));
    if (!pair) return false;
    const ownersKill = gk === pair.owner.id;
    if (ownersKill) {
      pair.banner.ours++;
      addGuildContribution(pair.owner, B.contributionPerKill);
    } else pair.banner.theirs++;
    this.h.emit({ type: 'guildBanner', what: 'kill', killer: killer.name, victim: victim.name, ours: pair.banner.ours, theirs: pair.banner.theirs });
    this.h.emit({ type: 'progressChanged' });
    this.h.log('fight', `ป้ายสังหาร · ${killer.name}สังหาร${victim.name} (${pair.banner.ours} : ${pair.banner.theirs}) ไม่มีค่า PK`);
    return true;
  }

  // ───────────────────────── commands ─────────────────────────

  /** The current player's guild, if their rank may run the enemy list and banners; else a notice and null. */
  private officer(): GuildState | null {
    const g = this.h.world.progress.guild;
    if (!g) {
      this.h.notice('ท่านยังไม่มีกิลด์');
      return null;
    }
    if (!B.raiseRanks.includes(g.rank)) {
      this.h.notice('มีแต่เจ้ากิลด์และรองเจ้ากิลด์ที่สั่งการเรื่องกิลด์ศัตรูได้');
      return null;
    }
    return g;
  }

  private known(id: string): boolean {
    return !!simGuild(id) || this.guilds().some((g) => g.id === id);
  }

  readonly commands = {
    guildEnemyAdd: (c: { id: string }) => {
      const g = this.officer();
      if (!g) return;
      const id = String(c.id ?? '');
      if (id === g.id || !this.known(id)) return this.h.notice('ไม่พบกิลด์นี้');
      const list = (g.enemies ??= []);
      if (list.includes(id)) return this.h.notice('กิลด์นี้เป็นกิลด์ศัตรูอยู่แล้ว');
      if (list.length >= B.enemiesMax) return this.h.notice(`กิลด์ศัตรูมีได้ไม่เกิน ${B.enemiesMax} กิลด์`);
      list.push(id);
      const name = this.nameOf(id);
      this.h.emit({ type: 'guildBanner', what: 'enemyAdd', guild: name });
      this.h.emit({ type: 'progressChanged' });
      this.h.log('guild', `${g.name} ประกาศให้ ${name} เป็นกิลด์ศัตรู`);
      this.h.notice(`ประกาศ ${name} เป็นกิลด์ศัตรูแล้ว`);
    },

    guildEnemyRemove: (c: { id: string }) => {
      const g = this.officer();
      if (!g) return;
      const list = g.enemies ?? [];
      const i = list.indexOf(String(c.id ?? ''));
      if (i < 0) return this.h.notice('กิลด์นี้ไม่ได้เป็นกิลด์ศัตรู');
      if (g.banner && g.banner.target === list[i]) return this.h.notice('ปลดไม่ได้ขณะป้ายสังหารยังปักอยู่');
      const name = this.nameOf(list[i]!);
      list.splice(i, 1);
      this.h.emit({ type: 'guildBanner', what: 'enemyRemove', guild: name });
      this.h.emit({ type: 'progressChanged' });
      this.h.notice(`ปลด ${name} ออกจากกิลด์ศัตรูแล้ว`);
    },

    guildBannerRaise: (c: { id: string }) => {
      const g = this.officer();
      if (!g) return;
      const id = String(c.id ?? '');
      if (!(g.enemies ?? []).includes(id)) return this.h.notice('ปักป้ายสังหารได้เฉพาะกิลด์ศัตรู');
      if (g.banner && B.maxActive <= 1) return this.h.notice('ป้ายสังหารของกิลด์ยังปักอยู่');
      if ((g.bannerCool ?? 0) > 0)
        return this.h.notice(`ป้ายสังหารพร้อมอีกครั้งในอีก ${Math.ceil(g.bannerCool! / 60)} นาที`);
      if (g.coffer < B.cost) return this.h.notice(`คลังกิลด์ไม่พอ (ต้องใช้ ${B.cost.toLocaleString('en-US')} ตำลึง)`);
      g.coffer -= B.cost;
      const targetName = this.nameOf(id);
      g.banner = { target: id, targetName, left: B.duration, ours: 0, theirs: 0 };
      this.foeT = B.foeEvery / 3;
      this.h.emit({ type: 'guildBanner', what: 'raise', guild: g.name, target: targetName, left: B.duration });
      this.h.emit({ type: 'progressChanged' });
      this.h.log('sys', `กิลด์ ${g.name} ปักป้ายสังหารกิลด์ ${targetName} · ${B.duration / 60} นาที`);
      this.h.log('guild', `ปักป้ายสังหาร ${targetName} แล้ว สังหารสมาชิกกิลด์นั้นได้โดยไม่มีค่า PK`);
      this.h.notice(`ปักป้ายสังหารกิลด์ ${targetName} · ${B.duration / 60} นาที`);
    },
  };

  // ───────────────────────── the clock and the hunters ─────────────────────────

  step(dt: number): void {
    for (const g of this.guilds()) {
      if (g.banner) {
        g.banner.left -= dt;
        if (g.banner.left <= 0) this.end(g);
      } else if (g.bannerCool && g.bannerCool > 0) g.bannerCool = Math.max(0, g.bannerCool - dt);
    }
    this.hunters(dt);
  }

  private end(g: GuildState): void {
    const b = g.banner!;
    delete g.banner;
    g.bannerCool = B.cooldown;
    this.h.emit({ type: 'guildBanner', what: 'end', guild: g.name, target: b.targetName, ours: b.ours, theirs: b.theirs });
    this.h.emit({ type: 'progressChanged' });
    this.h.log('sys', `ป้ายสังหารของกิลด์ ${g.name} ต่อ ${b.targetName} ลดลงแล้ว · ${b.ours} : ${b.theirs}`);
  }

  private inWilds(x: number, y: number): boolean {
    const w = this.h.world;
    return !w.inSafeZone({ x, y }) && !w.interiors.inside;
  }

  private hunters(dt: number): void {
    const w = this.h.world;
    const p = w.player;
    const b = w.progress.guild?.banner;
    // A simulated guild only: a real guild's members are real players.
    const target = b && simGuild(b.target) ? b.target : null;
    for (const [id, gid] of this.foes) {
      const f = w.get(id);
      const far = f ? Math.hypot(f.x - p.x, f.y - p.y) > B.foeLeave : true;
      // The fallen lie a moment (the rival brain would raise them at 8 s), then leave.
      if (!f || (f.dead && f.deadT > 3) || gid !== target || (far && !f.dead)) {
        this.remove(id);
        continue;
      }
      if (f.dead) continue;
      // They keep hunting the player outside safe ground (the rival brain strikes while aggro lasts).
      if (!w.inSafeZone(p) && !p.dead) {
        f.rival!.homeX = p.x;
        f.rival!.homeY = p.y;
        f.rival!.aggroT = Math.max(f.rival!.aggroT, 1);
      } else f.rival!.aggroT = 0;
    }
    if (!target || p.dead || w.duel || !this.inWilds(p.x, p.y)) return;
    if ((this.foeT -= dt) > 0) return;
    this.foeT = B.foeEvery;
    if (this.foes.size >= B.foesMax) return;
    this.spawn(target);
  }

  private remove(id: number): void {
    const w = this.h.world;
    this.foes.delete(id);
    if (!w.get(id)) return;
    w.entities.delete(id);
    if (w.player.targetId === id) w.player.targetId = null;
  }

  private spawn(gid: string): void {
    const w = this.h.world;
    const p = w.player;
    const s = simGuild(gid)!;
    const present = new Set([...this.foes.keys()].map((id) => w.get(id)?.name));
    const pick = s.members.filter((m) => !present.has(m.name));
    if (!pick.length) return;
    const m = pick[Math.floor(this.h.random() * pick.length)]!;
    const build = ARENA_ROSTER.find((r) => r.name === m.name) ?? ARENA_ROSTER[0]!;
    for (let k = 0; k < 8; k++) {
      const a = this.h.random() * Math.PI * 2;
      const d = B.foeMin + this.h.random() * (B.foeMaxDist - B.foeMin);
      const x = p.x + Math.cos(a) * d;
      const y = p.y + Math.sin(a) * d;
      if (x < 1 || y < 1 || x > w.map.w - 1 || y > w.map.h - 1 || !freeAt(w.map, x, y) || !this.inWilds(x, y)) continue;
      const e = this.h.add({
        kind: 'rival',
        x,
        y,
        name: m.name,
        title: `กิลด์ ${s.name}`,
        sprite: build.sprite,
        speed: p.speed * 0.95,
        level: p.level,
        rival: {
          homeX: p.x,
          homeY: p.y,
          aggroT: 0,
          skillT: 2,
          skills: getArt(build.art)
            .skills.filter((id) => ['attack', 'break', 'power'].includes(getSkill(id).kind))
            .slice(0, 4),
        },
      });
      Object.assign(e, {
        maxHp: Math.round(p.maxHp * B.foeHp),
        hp: Math.round(p.maxHp * B.foeHp),
        maxQi: p.maxQi,
        qi: p.maxQi,
        atk: p.atk * B.foeAtk,
        def: p.def,
        magic: p.magic * B.foeAtk,
        magicDef: p.magicDef,
        hit: p.hit,
        magicHit: p.magicHit,
        dodge: p.dodge,
        crit: p.crit,
        pk: false,
        pkPoints: 0,
      });
      this.foes.set(e.id, gid);
      this.h.emit({ type: 'guildBanner', what: 'foe', id: e.id, name: e.name });
      this.h.notice(`${e.name} แห่งกิลด์ ${s.name} ตามล่าท่านอยู่ใกล้ๆ`);
      return;
    }
  }
}
